import React from 'react';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '../../../app/store/slices/uiSlice';

/**
 * StatusBadge Component
 * A reusable component for displaying status badges with different colors and labels
 *
 * @param {string} status - The status key
 * @param {object} customStyles - Optional custom styles object mapping status to Tailwind classes
 * @param {object} customLabels - Optional custom labels object mapping status to display text
 */
const StatusBadge = ({ status, customStyles = {}, customLabels = {} }) => {
  const darkMode = useSelector(selectDarkMode);

  // Default status styles - light mode
  const lightStatusStyles = {
    'active': 'bg-green-100 text-green-800',
    'inactive': 'bg-red-100 text-red-800',
    'pending': 'bg-yellow-100 text-yellow-800',
    'completed': 'bg-green-100 text-green-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    'cancelled': 'bg-red-100 text-red-800',
    'scheduled': 'bg-blue-100 text-blue-800',
    'confirmed': 'bg-green-100 text-green-800',
    'fully-vaccinated': 'bg-green-100 text-green-800',
    'partial': 'bg-yellow-100 text-yellow-800',
    'not-started': 'bg-gray-100 text-gray-800',
    'in-stock': 'bg-green-100 text-green-800',
    'low-stock': 'bg-yellow-100 text-yellow-800',
    'out-of-stock': 'bg-red-100 text-red-800',
    'expired': 'bg-red-100 text-red-800',
    'expiring-soon': 'bg-orange-100 text-orange-800',
    'answered': 'bg-green-100 text-green-800',
    'missed': 'bg-red-100 text-red-800',
    'busy': 'bg-yellow-100 text-yellow-800',
    'no-answer': 'bg-gray-100 text-gray-800',
  };

  // Default status styles - dark mode
  const darkStatusStyles = {
    'active': 'bg-green-900/30 text-green-400',
    'inactive': 'bg-red-900/30 text-red-400',
    'pending': 'bg-yellow-900/30 text-yellow-400',
    'completed': 'bg-green-900/30 text-green-400',
    'in-progress': 'bg-blue-900/30 text-blue-400',
    'cancelled': 'bg-red-900/30 text-red-400',
    'scheduled': 'bg-blue-900/30 text-blue-400',
    'confirmed': 'bg-green-900/30 text-green-400',
    'fully-vaccinated': 'bg-green-900/30 text-green-400',
    'partial': 'bg-yellow-900/30 text-yellow-400',
    'not-started': 'bg-gray-700 text-gray-300',
    'in-stock': 'bg-green-900/30 text-green-400',
    'low-stock': 'bg-yellow-900/30 text-yellow-400',
    'out-of-stock': 'bg-red-900/30 text-red-400',
    'expired': 'bg-red-900/30 text-red-400',
    'expiring-soon': 'bg-orange-900/30 text-orange-400',
    'answered': 'bg-green-900/30 text-green-400',
    'missed': 'bg-red-900/30 text-red-400',
    'busy': 'bg-yellow-900/30 text-yellow-400',
    'no-answer': 'bg-gray-700 text-gray-300',
  };

  // Default status labels
  const defaultStatusLabels = {
    'active': 'Active',
    'inactive': 'Inactive',
    'pending': 'Pending',
    'completed': 'Completed',
    'in-progress': 'In Progress',
    'cancelled': 'Cancelled',
    'scheduled': 'Scheduled',
    'confirmed': 'Confirmed',
    'fully-vaccinated': 'Fully Vaccinated',
    'partial': 'Partial',
    'not-started': 'Not Started',
    'in-stock': 'In Stock',
    'low-stock': 'Low Stock',
    'out-of-stock': 'Out of Stock',
    'expired': 'Expired',
    'expiring-soon': 'Expiring Soon',
    'answered': 'Answered',
    'missed': 'Missed',
    'busy': 'Busy',
    'no-answer': 'No Answer',
  };

  // Choose styles based on dark mode
  const defaultStatusStyles = darkMode ? darkStatusStyles : lightStatusStyles;
  const defaultFallback = darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';

  // Merge custom styles and labels with defaults
  const statusStyles = { ...defaultStatusStyles, ...customStyles };
  const statusLabels = { ...defaultStatusLabels, ...customLabels };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${
        statusStyles[status] || defaultFallback
      }`}
    >
      {statusLabels[status] || status}
    </span>
  );
};

export default StatusBadge;
